import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import permanentDstPlugin from '../../ts-utils/dayjs-permanent-dst'

dayjs.extend(utc)
dayjs.extend(timezone)
// Keeps zones that abolished their clock change reading correctly, and labelled
// the way people there label them, on a runtime whose timezone database still
// has the old rule — see ts-utils/dayjs-permanent-dst.
dayjs.extend(permanentDstPlugin)
// `z` (the zone abbreviation, e.g. PDT) only exists with advancedFormat.
dayjs.extend(advancedFormat)

/** Zone-local calendar date, used only for same-day comparison. */
const ISO_DATE = 'YYYY-MM-DD'

export const TIME_WITH_ZONE = 'h:mm A z'
export const WEEKDAY_DATE = 'dddd, MMM D, YYYY'
export const SHORT_DATE = 'MMM D, YYYY'
/** Year-less, weekday inline — the compact card line, e.g. 'Wed, Aug 19'. */
export const WEEKDAY_SHORT_DATE = 'ddd, MMM D'

export interface EventTimesArgs {
  /** ISO 8601 instant. */
  startsAt?: string | null
  /** ISO 8601 instant. Null when the event has no end. */
  endsAt?: string | null
  /**
   * The event's own zone, already resolved via resolveEventTimezone. Null when
   * the event carries none — the second line is then suppressed entirely.
   */
  eventZone?: string | null
  /**
   * The VIEWER's zone. Callers must pass the real device/browser zone; on
   * React Native this comes from expo-localization, NOT dayjs.tz.guess(),
   * which resolves to UTC under Hermes.
   */
  viewerZone: string
}

/**
 * One instant rendered for display.
 *
 * `time`/`date` are always in the VIEWER's zone — the clock the reader is
 * actually living on. `eventTime`/`eventDate` carry the event's own zone and
 * are populated only when it resolves to a different clock, so a viewer in the
 * event's own zone sees a single unambiguous line.
 */
export interface EventTimePoint {
  /** Viewer-zone time, e.g. '3:03 PM PDT'. */
  time: string
  /** Viewer-zone date, e.g. 'Thursday, Aug 6, 2026'. */
  date: string
  /** Viewer-zone weekday alone, e.g. 'Thu'. */
  weekday: string
  /** Viewer-zone short date, e.g. 'Aug 6, 2026'. */
  shortDate: string
  /** Viewer-zone weekday + year-less date, e.g. 'Thu, Aug 6'. */
  weekdayShortDate: string
  /** Event-zone time, e.g. '6:03 PM EDT'. Empty when zones agree. */
  eventTime: string
  /**
   * Event-zone date. Set only when the event's zone puts this instant on a
   * different calendar day than the viewer's — crossing midnight is exactly
   * when a bare time would mislead.
   */
  eventDate: string
  /** Event-zone short date. Same emptiness rule as `eventDate`. */
  eventShortDate: string
  /** True when the two zones render this instant differently. */
  zonesDiffer: boolean
}

export const EMPTY_POINT: EventTimePoint = {
  time: '',
  date: '',
  weekday: '',
  shortDate: '',
  weekdayShortDate: '',
  eventTime: '',
  eventDate: '',
  eventShortDate: '',
  zonesDiffer: false,
}

/**
 * Builds the display strings for a single instant.
 *
 * Zone equality is decided on FORMATTED OUTPUT rather than zone identifiers,
 * so an event authored in America/Vancouver viewed on an
 * America/Los_Angeles device prints one line, not two identical ones.
 */
export const formatEventTimePoint = ({
  instant,
  eventZone,
  viewerZone,
}: {
  instant: string
  eventZone?: string | null
  viewerZone: string
}): EventTimePoint => {
  const inViewerZone = dayjs(instant).tz(viewerZone)
  if (!inViewerZone.isValid()) {
    return EMPTY_POINT
  }

  const time = inViewerZone.format(TIME_WITH_ZONE)
  const date = inViewerZone.format(WEEKDAY_DATE)
  const shortDate = inViewerZone.format(SHORT_DATE)
  const weekday = inViewerZone.format('ddd')
  const weekdayShortDate = inViewerZone.format(WEEKDAY_SHORT_DATE)

  // No event zone means there is no second clock to compare against — the
  // viewer's line stands alone.
  if (!eventZone) {
    return { ...EMPTY_POINT, time, date, shortDate, weekday, weekdayShortDate }
  }

  const inEventZone = dayjs(instant).tz(eventZone)
  const eventTime = inEventZone.format(TIME_WITH_ZONE)
  const eventDate = inEventZone.format(WEEKDAY_DATE)
  const eventShortDate = inEventZone.format(SHORT_DATE)

  const zonesDiffer = eventTime !== time

  return {
    time,
    date,
    shortDate,
    weekday,
    weekdayShortDate,
    eventTime: zonesDiffer ? eventTime : '',
    // Only when the event's zone lands this instant on another calendar day;
    // otherwise the viewer-zone date above it already applies.
    eventDate: zonesDiffer && eventDate !== date ? eventDate : '',
    eventShortDate:
      zonesDiffer && eventShortDate !== shortDate ? eventShortDate : '',
    zonesDiffer,
  }
}

export interface EventTimes {
  start: EventTimePoint
  /** Null when the event has no end or the end fails to parse. */
  end: EventTimePoint | null
  /** True when start and end fall on the same VIEWER-zone calendar day. */
  sameDay: boolean
}

export const EMPTY_TIMES: EventTimes = {
  start: EMPTY_POINT,
  end: null,
  sameDay: true,
}

/**
 * Display strings for an event's start and end.
 *
 * Start and end stay SEPARATE points rather than one joined range string: a
 * multi-day event has to show two dates, and joining them produces a headline
 * that wraps and buries the start date.
 */
export const formatEventTimes = ({
  startsAt,
  endsAt,
  eventZone,
  viewerZone,
}: EventTimesArgs): EventTimes => {
  if (!startsAt) {
    return EMPTY_TIMES
  }

  const start = dayjs(startsAt).tz(viewerZone)
  if (!start.isValid()) {
    return EMPTY_TIMES
  }

  const rawEnd = endsAt ? dayjs(endsAt).tz(viewerZone) : null
  const end = rawEnd?.isValid() ? rawEnd : null

  return {
    start: formatEventTimePoint({ instant: startsAt, eventZone, viewerZone }),
    end:
      end && endsAt
        ? formatEventTimePoint({ instant: endsAt, eventZone, viewerZone })
        : null,
    // Compared on the FORMATTED calendar date, not via `isSame(…, 'day')`.
    //
    // `isSame` with a unit re-derives the day boundary internally and does not
    // reliably honour the timezone plugin's offset under Hermes: a Vancouver
    // event running 2026-08-19 17:09 → 2026-08-20 19:09 reported `true` on
    // device while the very same input reported `false` under Node/Jest. The
    // screen then drew the one-date layout beside two different printed dates.
    //
    // Comparing the strings the UI actually renders cannot drift from what the
    // reader sees, and behaves identically in both runtimes.
    //
    // An event with no end spans nothing, so it is trivially same-day —
    // callers use this to pick a one-date layout.
    sameDay: end ? end.format(ISO_DATE) === start.format(ISO_DATE) : true,
  }
}
